import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { DownloadSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { fmt$, inputCls } from "./shared";

const TYPE_LABEL = (t) => {
  if (t.startsWith("adjustment:")) return `Adjustment · ${t.split(":")[1].replace(/_/g, " ")}`;
  return { receipt: "Goods receipt", shipment: "Shipment", transfer_in: "Transfer in", transfer_out: "Transfer out" }[t] || t;
};

export function StockLedger({ products, locations, refreshKey }) {
  const [sku, setSku] = useState("");
  const [locId, setLocId] = useState("");
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const params = useCallback(() => ({ ...(sku ? { sku } : {}), ...(locId ? { location_id: locId } : {}), days }), [sku, locId, days]);
  useEffect(() => {
    api.get("/scm/stock-ledger", { params: params() }).then((r) => setData(r.data)).catch(() => {});
  }, [params, refreshKey]);

  const download = async () => {
    setBusy(true);
    try {
      const { data: blob } = await api.get("/scm/stock-ledger.csv", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `stock-ledger-${sku || "all"}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const rows = data ? [...data.rows].reverse() : [];
  const opening = data && sku ? data.opening[sku] : null;
  const current = data && sku ? data.current[sku] : null;

  return (
    <div data-testid="ledger-panel">
      <div className="grid grid-cols-2 md:grid-cols-[1fr_220px_140px_auto] gap-px bg-zinc-200 border-b border-zinc-950">
        <select value={sku} onChange={(e) => setSku(e.target.value)} data-testid="ledger-sku-select" className={inputCls}>
          <option value="">All SKUs (product-level balance)</option>
          {products.map((p) => <option key={p.id} value={p.sku}>{`${p.sku} · ${p.name}`}</option>)}
        </select>
        <select value={locId} onChange={(e) => setLocId(e.target.value)} data-testid="ledger-location-select" className={inputCls}>
          <option value="">All locations</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{`${l.code} · ${l.name}`}</option>)}
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="ledger-days-select" className={inputCls}>
          {[7, 30, 90, 365, 3650].map((d) => <option key={d} value={d}>{d === 3650 ? "All time" : `Last ${d} days`}</option>)}
        </select>
        <button onClick={download} disabled={busy} className="bg-zinc-950 text-white text-xs font-semibold px-5 hover:bg-[#FF3E00] flex items-center justify-center gap-1 disabled:opacity-40" data-testid="ledger-csv-btn">
          <DownloadSimple size={14} /> DOWNLOAD CSV
        </button>
      </div>
      <div className="px-5 py-2 text-[10px] font-mono text-zinc-500 border-b border-zinc-200 flex flex-wrap gap-4" data-testid="ledger-meta">
        <span>{rows.length} movement{rows.length === 1 ? "" : "s"}</span>
        {sku && <span>OPENING <span className="text-zinc-950 font-semibold">{opening}</span></span>}
        {sku && <span>ON HAND{locId ? " @ LOCATION" : ""} <span className="text-zinc-950 font-semibold">{current}</span></span>}
        {!locId && <span className="text-zinc-400">Transfers net to zero at product level — pick a location to see them</span>}
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">Date</th>
            <th className="text-left px-4 py-2.5">Type</th>
            <th className="text-left px-4 py-2.5">Reference</th>
            <th className="text-left px-4 py-2.5">SKU</th>
            <th className="text-left px-4 py-2.5">Location</th>
            <th className="text-right px-4 py-2.5">In</th>
            <th className="text-right px-4 py-2.5">Out</th>
            <th className="text-right px-4 py-2.5">Balance</th>
            <th className="text-right px-4 py-2.5">Unit Cost</th>
            <th className="text-left px-4 py-2.5">By</th>
          </tr>
        </thead>
        <tbody data-testid="ledger-tbody">
          {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-6 text-xs text-zinc-400 font-mono">No stock movements in this window.</td></tr>}
          {rows.map((r, i) => (
            <tr key={`${r.ref_no}-${r.sku}-${r.type}-${i}`} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`ledger-row-${r.ref_no}-${r.sku}`}>
              <td className="px-4 py-2 tnum text-xs text-zinc-500">{r.ts.slice(0, 16).replace("T", " ")}</td>
              <td className="px-4 py-2 text-xs">{TYPE_LABEL(r.type)}</td>
              <td className="px-4 py-2 tnum text-xs">{r.ref_no}<div className="text-zinc-400">{r.source_no}</div></td>
              <td className="px-4 py-2 tnum text-xs">{r.sku} <span className="text-zinc-500">· {r.product_name}</span></td>
              <td className="px-4 py-2 tnum text-xs text-zinc-500">{r.location_code}</td>
              <td className="px-4 py-2 text-right tnum text-emerald-700">{r.qty_delta > 0 ? `+${r.qty_delta}` : ""}</td>
              <td className="px-4 py-2 text-right tnum text-[#D0021B]">{r.qty_delta < 0 ? r.qty_delta : ""}</td>
              <td className="px-4 py-2 text-right tnum font-semibold" data-testid={`ledger-balance-${r.ref_no}-${r.sku}`}>{r.balance_after}</td>
              <td className="px-4 py-2 text-right tnum text-zinc-500">{r.unit_cost === null || r.unit_cost === undefined ? "—" : fmt$(r.unit_cost)}</td>
              <td className="px-4 py-2 text-xs text-zinc-500">{r.actor}{r.note ? <div className="text-zinc-400">{r.note}</div> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
